import React, { useState, useCallback, useMemo } from "react";
import styles from "./Product.module.css";
import AddToCartControl from "../UILibrary/AddToCartControl";
import { IProductSummaryProps } from "@model/Products.model";
import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";


const ProductSummary: React.FC<IProductSummaryProps> = ({
  title,
  description,
  images,
  price,
  currency,
  quantity = 1,
  stock,
  maxPerUser,
  onAddToCart,

  addDisabled,
  helperText,
  onQuantityChangeExternal,
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [qty, setQty] = useState<number>(Math.max(1, Number(quantity) || 1));
  const [localError, setLocalError] = useState<string>(""); 
  const [loading, setLoading] = useState<boolean>(false);

  const isOutOfStock = (Number(stock) || 0) <= 0;

  const disabled = useMemo(
    () => Boolean(addDisabled) || isOutOfStock || !!localError || loading,
    [addDisabled, isOutOfStock, localError, loading]
  );

  const effectiveHelper =
    helperText || localError || (isOutOfStock ? "Out of stock" : "");

  const handleQtyChange = useCallback(
    (n: number) => {
      const v = Math.max(1, Number(n) || 1);
      setQty(v);
      onQuantityChangeExternal?.(v);
    },
    [onQuantityChangeExternal]
  );

  const { buildImageSrc } = useImageUploadRules({
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  const resolveSrc = useCallback(
    (v: any): string =>
      buildImageSrc(typeof v === "string" ? v : v?.url ?? null) || "",
    [buildImageSrc]
  );

  const activeSrc = useMemo(() => {
    const active = images?.[activeIdx];
    return resolveSrc(active);
  }, [images, activeIdx, resolveSrc]);

  const safeStockMax =
    typeof stock === "number" && Number.isFinite(stock) && stock > 0
      ? stock
      : Number.MAX_SAFE_INTEGER;

  const safeUserCap =
    typeof maxPerUser === "number" && Number.isFinite(maxPerUser) && maxPerUser > 0
      ? maxPerUser
      : Number.MAX_SAFE_INTEGER;

  const maxAllowed = Math.min(safeStockMax, safeUserCap);

  const handleAddToCart = useCallback(
    (q: number) => {
      if (disabled) return;
      setLoading(true);
      try {
        onAddToCart?.(q);
      } finally {
        setLoading(false);
      }
    },
    [disabled, onAddToCart]
  );

  return (
    <section className={styles.productSummarySP} data-testid="product-summary">
      {/* Gallery */}
      <div className={styles.gallerySP}>
        {activeSrc ? (
          <img
            className={styles.mainImageSP}
            src={activeSrc}
            alt={title}
            loading="eager"
          />
        ) : (
          <div className={styles.imagePlaceholderSP} aria-label="No image" />
        )}

        {!!images?.length && (
          <div className={styles.thumbRowSP} role="list">
            {images.map((src, i) => {
              const thumb = resolveSrc(src);
              return (
                <button
                  key={(typeof src === "string" ? src : src) ?? String(i)}
                  type="button"
                  role="listitem"
                  aria-label={`Show image ${i + 1}`}
                  className={`${styles.thumbSP} ${
                    i === activeIdx ? styles.thumbActiveSP : ""
                  }`}
                  onClick={() => setActiveIdx(i)}
                >
                  {thumb ? (
                    <img src={thumb} alt={`${title} thumbnail ${i + 1}`} />
                  ) : (
                    <div className={styles.thumbPlaceholderSP} aria-hidden />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary / CTA */}
      <div className={styles.summaryTextSP}>
        <h1 className={styles.titleSP} data-testid="product-title">
          {title}
        </h1>

        <p
          className={styles.shortDescriptionSP}
          data-testid="product-description"
        >
          {description}
        </p>

        {typeof price === "number" && (
          <div className={styles.priceWrapSP}>
            <span className={styles.priceSP} data-testid="product-price">
              {currency ?? "$"} {price.toFixed(2)}
            </span>
          </div>
        )}

        <div className={styles.addToCartWrapSP}>
          <AddToCartControl
            quantity={qty}
            onQuantityChange={handleQtyChange}
            onError={setLocalError}
            onClick={handleAddToCart}
            min={1}
            max={maxAllowed}
            step={1}
            error={effectiveHelper}
            loading={loading}
            customWrapperClass={styles.addToCartSP}
            quantityControlClass={styles.qtyControlSP}
            buttonClass={styles.addButtonSP}
            disabled={disabled}
          />
       
        </div>
      </div>
    </section>
  );
};

export default ProductSummary;
